/**
 * AI Security notifications API client.
 *
 * Wraps the tenant-scoped `/ai-security/notifications` endpoints on cnapp-api,
 * which are the system of record for both the notification log and this
 * caller's read/dismissed state. Payloads are returned in their snake_case wire
 * shape; the producer hook (`hooks/useAiSecurityNotifications.ts`) maps them
 * onto `Message`.
 */

import { apiFetch, apiFetchVoid } from './core'

// ---- Backend payload shapes (snake_case) -----------------------------------

/** Route-free semantic descriptor cnapp-api derives for identity notifications;
 *  the frontend — not either backend — owns the URL shape and maps this to a
 *  path (see `hooks/useAiSecurityNotifications.ts`,
 *  `pages/ai-applications/_shared/deeplinks.ts`). Absent for concern rows and
 *  any identity row from a backend that predates this field. */
export type ApiAiLinkDescriptor =
  | { kind: 'ai_identity'; identity_id: string; identity_name?: string }
  | { kind: 'ai_identity_risk_list'; risk_levels?: string[] }
  | { kind: 'ai_shadow_ai'; destination_domain: string }

export type ApiAiSecurityNotification = {
  /** `ai_notifications.id`, a JSON **number** so it reconciles against
   *  `Message.sourceId` with `===`. */
  id: number
  notification_type: string
  title: string
  body: string
  /** Only High and Critical are notified (FR-6.1.x). */
  severity: 'high' | 'critical'
  /** In-app route, re-resolved server-side from live triage state on every read,
   *  so it can change for a row whose `created_at` never moves. Identity rows
   *  no longer carry a route here — the API COALESCEs to `''` for them, so this
   *  stays a non-null string and `link_descriptor` is the one to check first. */
  deep_link_path: string
  /** Present for identity rows; the frontend derives the URL from this rather
   *  than from `deep_link_path`. */
  link_descriptor?: ApiAiLinkDescriptor
  /** RFC3339. The event clock; compared as a Date instant, never as a string. */
  created_at: string
  /** RFC3339 when this caller has read the row, else null. */
  read_at: string | null
}

export type ApiAiSecurityNotificationsResponse = {
  notifications: ApiAiSecurityNotification[]
  has_more: boolean
}

export type FetchAiSecurityNotificationsOptions = {
  /** Omit to take the server default of 100. The server does **not** clamp — a
   *  present `limit` outside 1..200, non-integer, or empty is a `400`. */
  limit?: number
  /** Lower bound on `created_at`. No production caller: the bell producer omits
   *  it because a browser-clock value can only narrow the server's 7-day floor
   *  (see `useAiSecurityNotifications`). Kept for a caller with a real
   *  server-clock-derived bound, e.g. a future incremental sync. */
  since?: Date
  signal?: AbortSignal
}

const NOTIFICATIONS_PATH = '/ai-security/notifications'

const buildQuery = (opts: FetchAiSecurityNotificationsOptions): string => {
  const q = new URLSearchParams()
  if (opts.limit !== undefined) q.set('limit', String(opts.limit))
  if (opts.since !== undefined) q.set('since', opts.since.toISOString())
  const qs = q.toString()
  return qs ? `?${qs}` : ''
}

/** Fetch this caller's not-yet-dismissed High/Critical notifications. */
export const fetchAiSecurityNotifications = async (
  tenant: string,
  opts: FetchAiSecurityNotificationsOptions = {},
): Promise<ApiAiSecurityNotificationsResponse> => {
  const res = await apiFetch<ApiAiSecurityNotificationsResponse>(
    `${NOTIFICATIONS_PATH}${buildQuery(opts)}`,
    { signal: opts.signal },
    tenant,
  )
  // Guard against a nil slice marshaled as JSON null (Go footgun); the backend
  // sends [] but a null would crash every consumer of the list.
  return {
    notifications: res.notifications ?? [],
    has_more: res.has_more ?? false,
  }
}

// ---- Mutations (204 No Content, empty request body) ------------------------

const notificationPath = (id: number, action: 'read' | 'dismiss'): string =>
  `${NOTIFICATIONS_PATH}/${encodeURIComponent(String(id))}/${action}`

/** Mark one notification read for the acting user; first read wins. */
export const markAiSecurityNotificationRead = (
  tenant: string,
  id: number,
): Promise<void> =>
  apiFetchVoid(notificationPath(id, 'read'), { method: 'POST' }, tenant)

/** Dismiss one notification for the acting user; the server stops returning it,
 *  which is what makes the dismissal survive the next poll. */
export const dismissAiSecurityNotification = (
  tenant: string,
  id: number,
): Promise<void> =>
  apiFetchVoid(notificationPath(id, 'dismiss'), { method: 'POST' }, tenant)

/** Mark every not-yet-dismissed notification read for the acting user. */
export const markAllAiSecurityNotificationsRead = (
  tenant: string,
): Promise<void> =>
  apiFetchVoid(`${NOTIFICATIONS_PATH}/read-all`, { method: 'POST' }, tenant)

/** Dismiss every notification for the acting user — the server-side half of
 *  "Clear all", without which the list resurrects within 60 s. */
export const dismissAllAiSecurityNotifications = (
  tenant: string,
): Promise<void> =>
  apiFetchVoid(`${NOTIFICATIONS_PATH}/dismiss-all`, { method: 'POST' }, tenant)
